import { Archive, ArrowLeftRight, CalendarDays, Cog, DoorOpen, Eye, FileSignature, House, ListX, Megaphone, Power, Repeat, ScrollText, Settings, ShieldCheck, SpellCheck, TextCursorInput, UserPlus, Users, Wrench } from 'lucide-react';
import { canEdit } from '@nest-recruit/shared';
import type { router as AppRouter } from '../../router';
import { registerCommands, type Command } from '../../shell/command-registry';

/**
 * The admin module's palette entries, registered from this module rather than by
 * editing `shell-commands.ts` — 06-qol §1's registration pattern.
 *
 * USER-01 to USER-06 are reachable through the module bar's "Admin" destination and
 * its menu; these are the admin screens somebody goes looking for by name — a calendar
 * because a holiday is coming and an SLA is about to count it, the offer letters when
 * the wording needs a change (S23), the event log because a number came back wrong.
 *
 * Each names the §L23 object it needs, so nobody sees a destination they would be
 * refused at. The filtering happens before display, not after a click.
 */
export function registerAdminCommands(router: typeof AppRouter): () => void {
  const commands: Command[] = [
    {
      id: 'admin.overview',
      label: 'Admin overview',
      section: 'navigate',
      context: 'Screen · ADMIN-21 · what needs your attention',
      permission: 'admin_config',
      icon: House,
      keywords: ['admin home', 'attention', 'health', 'summary'],
      run: () => void router.navigate({ to: '/admin/overview' }),
    },
    {
      id: 'admin.people',
      label: 'People',
      section: 'navigate',
      context: 'Screen · USER-01 · everyone with a login',
      permission: 'user_management',
      icon: Users,
      keywords: ['users', 'staff', 'accounts', 'deactivate', 'login'],
      run: () => void router.navigate({ to: '/admin/users' }),
    },
    {
      id: 'admin.invite',
      label: 'Invite a person',
      section: 'action',
      context: 'USER-03 · send a sign-in invitation',
      permission: 'user_management',
      canSee: (session) => canEdit(session, 'user_management'),
      icon: UserPlus,
      keywords: ['invite', 'new user', 'add user', 'account'],
      run: () => void router.navigate({ to: '/admin/invite' }),
    },
    {
      id: 'admin.roles',
      label: 'Roles and permissions',
      section: 'navigate',
      context: 'Screen · USER-04',
      permission: 'user_management',
      icon: ShieldCheck,
      keywords: ['role', 'permission', 'access', 'matrix', 'field'],
      run: () => void router.navigate({ to: '/admin/roles' }),
    },
    {
      id: 'admin.delegation',
      label: 'Arrange cover',
      section: 'navigate',
      // F2.11's actor is "Self, HR Lead": everyone arranges their own cover (tester D28).
      context: 'Screen · who approves for you while you are away',
      icon: Repeat,
      keywords: ['delegation', 'delegate', 'away', 'leave', 'cover', 'approver'],
      run: () => void router.navigate({ to: '/admin/delegation' }),
    },
    {
      id: 'admin.rooms',
      label: 'Interview rooms',
      section: 'navigate',
      context: 'Screen · S19',
      permission: 'hiring_setup',
      icon: DoorOpen,
      keywords: ['room', 'meeting room', 'venue', 'interview'],
      run: () => void router.navigate({ to: '/admin/rooms' }),
    },
    {
      id: 'admin.accessLog',
      label: 'Access log',
      section: 'navigate',
      context: 'Screen · who opened what',
      permission: 'audit_log',
      icon: Eye,
      keywords: ['audit', 'access', 'who viewed', 'salary viewed', 'log'],
      run: () => void router.navigate({ to: '/admin/audit' }),
    },
    {
      id: 'admin.calendars',
      label: 'Working hours and holidays',
      section: 'navigate',
      context: 'Screen · ADMIN-10',
      permission: 'hiring_setup',
      icon: CalendarDays,
      keywords: ['calendar', 'holiday', 'working days', 'hours', 'sla', 'weekend', 'timezone'],
      run: () => void router.navigate({ to: '/admin/calendars' }),
    },
    {
      id: 'admin.retention',
      label: 'Retention and archives',
      section: 'navigate',
      context: 'Screen · S30 · anonymisation, monthly archive',
      permission: 'admin_config',
      icon: Archive,
      keywords: ['retention', 'anonymise', 'gdpr', 'archive', 'delete data', 'dry run', 'consent', '90 days'],
      run: () => void router.navigate({ to: '/admin/retention', search: {} }),
    },
    {
      id: 'admin.masterSettings',
      label: 'Master settings',
      section: 'navigate',
      context: 'Screen · ADMIN-17',
      permission: 'admin_config',
      icon: Power,
      keywords: ['modules', 'switch', 'turn on', 'turn off', 'hide', 'features', 'enable', 'disable'],
      run: () => void router.navigate({ to: '/admin/master-settings' }),
    },
    {
      id: 'admin.settings',
      label: 'System settings',
      section: 'navigate',
      context: 'Screen · ADMIN-15',
      permission: 'admin_config',
      icon: Settings,
      keywords: ['settings', 'currency', 'date format', 'number format', 'lakh', 'fiscal year', 'file size', 'upload limit', 'final selection', 'hold', 'timezone'],
      run: () => void router.navigate({ to: '/admin/settings' }),
    },
    {
      id: 'admin.sources',
      label: 'Sources',
      section: 'navigate',
      context: 'Screen · ADMIN-08',
      permission: 'hiring_setup',
      icon: Megaphone,
      keywords: ['source', 'job board', 'bdjobs', 'linkedin', 'channel', 'where candidates come from'],
      run: () => void router.navigate({ to: '/admin/sources' }),
    },
    {
      id: 'admin.toolNames',
      label: 'Tools and systems',
      section: 'navigate',
      context: 'Screen · ADMIN-19 · what box 1 of the ideal candidate offers',
      permission: 'hiring_setup',
      icon: Wrench,
      keywords: ['tool', 'system', 'software', 'misspelt', 'typo', 'merge', 'google sheets', 'ideal candidate'],
      run: () => void router.navigate({ to: '/admin/tool-names' }),
    },
    {
      id: 'admin.staff',
      label: 'Staff list',
      section: 'navigate',
      context: 'Screen · Admin',
      permission: 'hiring_setup',
      icon: Users,
      keywords: ['staff', 'employees', 'team', 'reports to', 'manager', 'replacement', 'upload'],
      run: () => void router.navigate({ to: '/admin/staff' }),
    },
    {
      id: 'admin.fxRates',
      label: 'Exchange rates',
      section: 'navigate',
      context: 'Screen · S31 · Show converted',
      permission: 'admin_config',
      icon: ArrowLeftRight,
      keywords: ['exchange', 'fx', 'currency', 'rate', 'convert', 'usd', 'gbp', 'bdt'],
      run: () => void router.navigate({ to: '/admin/fx-rates' }),
    },
    {
      id: 'admin.customFields',
      label: 'Custom fields',
      section: 'navigate',
      context: 'Screen · ADMIN-13',
      permission: 'admin_config',
      icon: TextCursorInput,
      keywords: ['custom field', 'extra field', 'more details', 'form', 'attribute'],
      run: () => void router.navigate({ to: '/admin/custom-fields' }),
    },
    {
      id: 'admin.nameMatching',
      label: 'Name matching',
      section: 'navigate',
      context: 'Screen · §L2 spellings',
      permission: 'admin_config',
      icon: SpellCheck,
      keywords: ['name', 'spelling', 'transliteration', 'duplicate', 'mohammad', 'md'],
      run: () => void router.navigate({ to: '/admin/name-matching' }),
    },
    {
      id: 'admin.reasons',
      label: 'Rejection and withdrawal reasons',
      section: 'navigate',
      context: 'Screen · ADMIN-07',
      permission: 'hiring_setup',
      icon: ListX,
      keywords: ['reason', 'rejection', 'withdrawal', 'regret', 'pool', 'taxonomy'],
      run: () => void router.navigate({ to: '/admin/reasons' }),
    },
    {
      id: 'admin.offerLetters',
      label: 'Offer letters',
      section: 'navigate',
      context: 'Screen · ADMIN-16',
      permission: 'hiring_setup',
      icon: FileSignature,
      keywords: ['offer', 'letter', 'template', 'signatory', 'salary', 'contract'],
      run: () => void router.navigate({ to: '/admin/offer-letters' }),
    },
    {
      id: 'admin.eventLog',
      label: 'Event log',
      section: 'navigate',
      context: 'Screen · S7',
      permission: 'audit_log',
      icon: ScrollText,
      keywords: ['events', 'audit', 'history', 'debug', 'inspector', 'trail'],
      run: () => void router.navigate({ to: '/admin/events' }),
    },
    {
      // Admin redesign batch 2 (owner Q3): our own page over the queues, which replaced "Queues ↗".
      id: 'admin.backgroundWork',
      label: 'Background work',
      section: 'navigate',
      context: 'Screen · ADMIN-24 · queues and scheduled work',
      permission: 'admin_config',
      icon: Cog,
      keywords: ['queues', 'jobs', 'background', 'stuck', 'retry', 'scheduled', 'cron', 'bull board'],
      run: () => void router.navigate({ to: '/admin/background-work' }),
    },
  ];

  return registerCommands(commands);
}
